import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import React, { useState, useEffect, useMemo } from 'react';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CircularProgress from '@mui/material/CircularProgress';

import { useNavigate } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import Message from '../components/Message';

import VIP_BG from '../img/VIP-BG.jpg';
import CD_LOGO from '../img/cd-logo.png';

import { adminLogin } from '../actions/adminUserActions';

// iPad-first breakpoints
// iPad Portrait: 768px - 1024px
// iPad Landscape: 1024px - 1366px
const BREAKPOINTS = {
  mobile: 767, // Below iPad
  ipadPortrait: { min: 768, max: 1023 },
  ipadLandscape: { min: 1024, max: 1366 },
  desktop: 1367, // Above iPad
};

const AdminLoginScreen = () => {
  const dispatch = useDispatch();
  let navigate = useNavigate();

  // iPad-first responsive breakpoints
  const isIpadPortrait = useMediaQuery(
    `(min-width:${BREAKPOINTS.ipadPortrait.min}px) and (max-width:${BREAKPOINTS.ipadPortrait.max}px)`,
  );
  const isIpadLandscape = useMediaQuery(
    `(min-width:${BREAKPOINTS.ipadLandscape.min}px) and (max-width:${BREAKPOINTS.ipadLandscape.max}px)`,
  );
  const isIpad = isIpadPortrait || isIpadLandscape;
  const isDesktop = useMediaQuery(`(min-width:${BREAKPOINTS.desktop}px)`);
  const isMobile = useMediaQuery(`(max-width:${BREAKPOINTS.mobile}px)`);

  const [values, setValues] = useState({
    username: '',
    password: '',
    showPassword: false,
  });

  const adminUserLogin = useSelector((state) => state.adminUserLogin);
  const { loading, adminUserInfo } = adminUserLogin;

  const alert = useSelector((state) => state.alert);
  const { msg } = alert;

  useEffect(() => {
    if (adminUserInfo) {
      navigate('/');
    }
  }, [adminUserInfo, navigate]);

  const handleChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value });
  };

  const handleClickShowPassword = () => {
    setValues({
      ...values,
      showPassword: !values.showPassword,
    });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const onSubmit = (e) => {
    e.preventDefault();
    dispatch(adminLogin(values.username, values.password));
  };

  // iPad-first responsive logo sizing
  const logoWidth = useMemo(() => {
    if (isIpadLandscape) return 260;
    if (isIpadPortrait) return 240;
    if (isDesktop) return 230;
    return 200; // mobile
  }, [isIpadPortrait, isIpadLandscape, isDesktop]);

  // Card width optimized for iPad
  const getCardWidth = () => {
    if (isIpadLandscape) return 400;
    if (isIpadPortrait) return 380;
    if (isDesktop) return 360;
    return '100%'; // mobile - full width with container padding
  };

  // Typography sizing optimized for iPad readability
  const getTitleFontSize = () => {
    if (isIpadLandscape) return '1.75rem';
    if (isIpadPortrait) return '1.625rem';
    if (isDesktop) return '1.5rem';
    return '1.375rem'; // mobile
  };

  // Touch-friendly input sizing (min 44px for iOS guidelines)
  const getInputSize = () => {
    // Return 'medium' for iPad to ensure comfortable touch targets
    if (isIpad) return 'medium';
    if (isDesktop) return 'medium';
    return 'medium'; // Keep medium for mobile too for better touch experience
  };

  // Input styling for iPad - compact for login form
  const getInputStyles = () => {
    return {
      width: '100%',
      marginBottom: isIpad ? '12px' : '10px',
      '& .MuiOutlinedInput-root': {
        fontSize: isIpad ? '1rem' : '0.9375rem',
      },
      '& .MuiInputLabel-root': {
        fontSize: isIpad ? '1rem' : '0.9375rem',
      },
      '& .MuiOutlinedInput-input': {
        // Prevent zoom on iOS Safari when focusing inputs
        fontSize: '16px',
      },
    };
  };

  // Touch-friendly button sizing - compact for login
  const getButtonStyles = () => {
    if (isIpadLandscape) {
      return {
        minHeight: 46,
        fontSize: '1.05rem',
        py: 1,
      };
    }
    if (isIpadPortrait) {
      return {
        minHeight: 44,
        fontSize: '1rem',
        py: 1,
      };
    }
    if (isDesktop) {
      return {
        minHeight: 42,
        fontSize: '0.9375rem',
        py: 0.875,
      };
    }
    // Mobile - still touch friendly
    return {
      minHeight: 42,
      fontSize: '0.9375rem',
      py: 0.875,
    };
  };

  // Icon button size for password toggle
  const getIconButtonSize = () => {
    if (isIpad) return 'large';
    return 'medium';
  };

  return (
    <Box
      sx={{
        background: `url(${VIP_BG}) center center no-repeat`,
        backgroundSize: 'cover',
        boxShadow: 'inset 0 0 0 2000px rgba(0, 0, 0, 0.3)',
        // Prevent content shift on orientation change
        overflow: 'hidden',
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // iPad-first padding
          px: isIpad ? 4 : isDesktop ? 3 : 2,
          py: isIpad ? 4 : 2,
        }}
      >
        <Card
          variant="outlined"
          sx={{
            width: getCardWidth(),
            maxWidth: '100%',
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: isIpad ? 3 : 2,
            boxShadow: isIpad
              ? '0 8px 32px rgba(0, 0, 0, 0.15)'
              : '0 4px 16px rgba(0, 0, 0, 0.1)',
            // Smooth transition for orientation changes
            transition: 'width 0.2s ease-in-out',
          }}
        >
          <CardContent
            sx={{
              textAlign: 'center',
              // Compact padding for login form
              padding: isIpadLandscape
                ? '24px 32px'
                : isIpadPortrait
                  ? '22px 28px'
                  : isDesktop
                    ? '22px 24px'
                    : '20px 18px',
            }}
          >
            {/* Logo - iPad optimized sizing */}
            <Box
              sx={{
                mb: isIpad ? 1.5 : 1,
              }}
            >
              <img
                src={CD_LOGO}
                width={logoWidth}
                alt="Club Dynasty Logo"
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  // Smooth scaling for orientation changes
                  transition: 'width 0.2s ease-in-out',
                }}
              />
            </Box>

            {/* Title - iPad optimized typography */}
            <Typography
              variant="h2"
              sx={{
                fontSize: getTitleFontSize(),
                fontWeight: 600,
                mb: isIpad ? 2 : 1.5,
                color: 'text.primary',
                // Improve text rendering on iPad
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
              }}
            >
              Admin Login
            </Typography>

            <form onSubmit={(e) => onSubmit(e)}>
              {/* Username field - Touch optimized */}
              <TextField
                size={getInputSize()}
                variant="outlined"
                type="text"
                value={values.username}
                onChange={handleChange('username')}
                label="Username"
                autoComplete="username"
                sx={getInputStyles()}
              />

              {/* Password field - Touch optimized */}
              <TextField
                variant="outlined"
                size={getInputSize()}
                type={values.showPassword ? 'text' : 'password'}
                value={values.password}
                onChange={handleChange('password')}
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        size={getIconButtonSize()}
                        sx={{
                          // Ensure comfortable touch target
                          minWidth: isIpad ? 48 : 40,
                          minHeight: isIpad ? 48 : 40,
                          // Prevent double-tap zoom on iOS
                          WebkitTapHighlightColor: 'transparent',
                          touchAction: 'manipulation',
                        }}
                      >
                        {values.showPassword ? (
                          <VisibilityOff
                            sx={{ fontSize: isIpad ? '1.5rem' : '1.25rem' }}
                          />
                        ) : (
                          <Visibility
                            sx={{ fontSize: isIpad ? '1.5rem' : '1.25rem' }}
                          />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                label="Password"
                sx={getInputStyles()}
              />

              {/* Submit button - Touch-friendly sizing for iPad */}
              <Button
                variant="contained"
                disableElevation
                type="submit"
                fullWidth
                sx={{
                  ...getButtonStyles(),
                  mt: isIpad ? 0.5 : 0.25,
                  borderRadius: isIpad ? 1.5 : 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  // Ensure touch target is comfortable on iPad
                  touchAction: 'manipulation',
                  // Prevent double-tap zoom on iOS
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                Login
                {loading && (
                  <>
                    &nbsp;
                    <CircularProgress
                      color="inherit"
                      size={isIpad ? 24 : 20}
                      thickness={5}
                    />
                  </>
                )}
              </Button>
            </form>
            {/* Lounge Access button - Premium glassmorphic design */}
            <Button
              variant="outlined"
              fullWidth
              onClick={() => navigate('/lounge-access')}
              sx={{
                ...getButtonStyles(),
                mt: isIpad ? 2 : 1.5,
                borderRadius: isIpad ? 1.5 : 1,
                textTransform: 'none',
                fontWeight: 600,
                position: 'relative',
                overflow: 'hidden',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
                backdropFilter: 'blur(10px)',
                border: '1.5px solid rgba(218, 165, 32, 0.4)',
                color: 'rgb(184, 134, 11)',
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255, 215, 0, 0.2), transparent)',
                  transition: 'left 0.5s ease-in-out',
                },
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(218, 165, 32, 0.12), rgba(255, 215, 0, 0.15))',
                  border: '1.5px solid rgba(218, 165, 32, 0.6)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(218, 165, 32, 0.3)',
                  '&::before': {
                    left: '100%',
                  },
                },
                '&:active': {
                  transform: 'translateY(0)',
                  boxShadow: '0 2px 8px rgba(218, 165, 32, 0.25)',
                },
              }}
            >
              Lounge Access
            </Button>
          </CardContent>
        </Card>

        {msg?.length > 0 && <Message />}
      </Container>
    </Box>
  );
};

export default AdminLoginScreen;
